<template>
	<div>
		<lazy-form-select v-if="type === 'select'"
						  :help="help"
						  v-model:error="errorMessage"
						  v-model:valid="isValid"
						  :placeholder="placeholder"
						  :inverted="inverted"
						  :label="label"
						  :clearable="clearable"
						  :disabled="disabled"
						  :unselect="inputProps.unselect"
						  :image-prop="inputProps.imageProp"
						  :icon-prop="inputProps.iconProp"
						  :text-prop="inputProps.textProp"
						  :subtitle-prop="inputProps.subtitleProp"
						  :value-prop="inputProps.valueProp"
						  :color="color"
						  :name="name"
						  :info="info"
						  :filterable="Array.isArray(options) && options.length > 10"
						  :hide-details="hideDetails"
						  :autofocus="autofocus"
						  :rules="rules"
						  v-model="submitValue"
						  :options="options" @key-enter="handleEnter"/>
		<lazy-form-input v-else-if="type === 'input'"
						 :placeholder="placeholder"
						 :help="help"
						 v-model:error="errorMessage"
						 v-model:valid="isValid"
						 :autofocus="autofocus"
						 :rules="rules"
						 :info="info"
						 :clearable="clearable"
						 :disabled="disabled"
						 :name="name"
						 :mask="inputProps.mask"
						 :hide-details="hideDetails"
						 v-model="submitValue"
						 :inverted="inverted"
						 :label="label" @key-enter="handleEnter"/>
		<lazy-form-number-input v-else-if="type === 'number'"
								:disabled="disabled"
								:name="name"
								:hide-details="hideDetails"
								v-model="submitValue"
								:rules="rules"
								:label="label"
								v-model:error="errorMessage"
								v-model:valid="isValid"
								:no-separators="inputProps.noSeparators"
								:suffix="inputProps.suffix"
								:clearable="clearable"
								:autofocus="autofocus"
								:inverted="inverted"
								:placeholder="placeholder"
								:info="info"
								:help="help" @key-enter="handleEnter"/>
		<lazy-form-price-input v-else-if="type === 'cents'"
							   :disabled="disabled"
							   :name="name"
							   :hide-details="hideDetails"
							   v-model="submitValue"
							   :label="label"
							   :rules="rules"
							   v-model:error="errorMessage"
							   v-model:valid="isValid"
							   :decimals="inputProps.decimals"
							   :clearable="clearable"
							   :autofocus="autofocus"
							   :inverted="inverted"
							   :placeholder="placeholder"
							   :help="help" @key-enter="handleEnter"
							   :price-info-text="inputProps.priceInfoText"/>
		<lazy-form-date-input v-else-if="type === 'date' || type === 'datetime'"
							  :disabled="disabled"
							  :name="name"
							  :rules="rules"
							  :hide-details="hideDetails"
							  v-model="submitValue"
							  :label="label"
							  :color="inputProps.color"
							  v-model:error="errorMessage"
							  v-model:valid="isValid"
							  :autofocus="autofocus"
							  :inverted="inverted"
							  :placeholder="placeholder"
							  :help="help"
							  :info="info"
							  :clearable="clearable"
							  :even-minutes="inputProps.evenMinutes"
							  :time="inputProps.time || type === 'datetime'" @key-enter="handleEnter">
			<template v-if="$slots.default" #default>
				<slot/>
			</template>
		</lazy-form-date-input>
		<lazy-form-time-input v-else-if="type === 'time'"
							  :disabled="disabled"
							  :name="name"
							  :rules="rules"
							  :hide-details="hideDetails"
							  v-model="submitValue"
							  :color="inputProps.color"
							  v-model:error="errorMessage"
							  v-model:valid="isValid"
							  :label="label"
							  :autofocus="autofocus"
							  :inverted="inverted"
							  :placeholder="placeholder"
							  :help="help"
							  :info="info"
							  :clearable="clearable" @key-enter="handleEnter">
			<slot/>
		</lazy-form-time-input>
		<form-codearea v-else-if="type === 'html'"
					   :placeholder="placeholder"
					   :help="help"
					   :info="info"
					   v-model="submitValue"
					   :disabled="disabled"
					   :clearable="clearable"
					   v-model:error="errorMessage"
					   v-model:valid="isValid"
					   :name="name"
					   :hide-details="hideDetails"
					   :autofocus="autofocus"
					   :rules="rules"
					   :inverted="inverted"
					   :label="label" @key-enter="handleEnter"/>
		<lazy-form-textarea v-else-if="type === 'text'"
							:placeholder="placeholder"
							:help="help"
							:info="info"
							v-model="submitValue"
							:disabled="disabled"
							v-model:error="errorMessage"
							v-model:valid="isValid"
							:name="name"
							:rows="inputProps.rows ?? 2"
							:clearable="clearable"
							:hide-details="hideDetails"
							:autofocus="autofocus"
							:rules="rules"
							:inverted="inverted"
							:label="label" @key-enter="handleEnter"/>
		<lazy-form-checkbox v-else-if="type === 'checkbox'"
							class="justify-end"
							:placeholder="placeholder"
							:help="help"
							:info="info"
							:disabled="disabled"
							v-model:error="errorMessage"
							v-model:valid="isValid"
							:name="name"
							:clearable="clearable"
							:hide-details="hideDetails"
							:inverted="inverted"
							:label="label"
							:autofocus="autofocus"
							:rules="rules"
							v-model="submitValue"/>
		<lazy-form-switch v-else-if="type === 'switch'"
						  :placeholder="placeholder"
						  :help="help"
						  :info="info"
						  :center="center"
						  :autofocus="autofocus"
						  :rules="rules"
						  :clearable="clearable"
						  v-model:error="errorMessage"
						  v-model:valid="isValid"
						  :disabled="disabled"
						  :name="name"
						  :hide-details="hideDetails"
						  v-model="submitValue"
						  :inverted="inverted"
						  :label="label"/>
		<lazy-form-remote-select v-else-if="type === 'lazy-select'"
								 :disabled="disabled"
								 :name="name"
								 :hide-details="hideDetails"
								 v-model="submitValue"
								 :label="label"
								 :autofocus="autofocus"
								 :rules="rules"
								 :inverted="inverted"
								 :color="color"
								 v-model:error="errorMessage"
								 v-model:valid="isValid"
								 :placeholder="placeholder"
								 :help="help"
								 :info="info"
								 :clearable="clearable"
								 :action="inputProps.action"
								 :data="inputProps.data"
								 :text-prop="inputProps.textProp"
								 :subtitle-prop="inputProps.subtitleProp"
								 :image-prop="inputProps.imageProp"
								 :value-prop="inputProps.valueProp"
								 :filterable="inputProps.filterable"
								 @key-enter="handleEnter"/>
		<lazy-form-color-input v-else-if="type === 'color'"
							   :placeholder="placeholder"
							   :help="help"
							   v-model="submitValue"
							   :disabled="disabled"
							   :name="name"
							   v-model:error="errorMessage"
							   v-model:valid="isValid"
							   :clearable="clearable"
							   :hide-details="hideDetails"
							   :autofocus="autofocus"
							   :rules="rules"
							   :info="info"
							   :inverted="inverted"
							   :label="label" @key-enter="handleEnter"/>
		<lazy-form-file-input v-else-if="type === 'image'"
							  :placeholder="placeholder"
							  :help="help"
							  only-images
							  v-model="submitValue"
							  :disabled="disabled"
							  v-model:error="errorMessage"
							  v-model:valid="isValid"
							  :name="name"
							  :info="info"
							  :clearable="clearable"
							  :hide-details="hideDetails"
							  :autofocus="autofocus"
							  :rules="rules"
							  :inverted="inverted"
							  :label="label"/>
	</div>
</template>

<script>
import Inputtable from "../../../mixins/Inputtable";
import Validatable from "../../../mixins/Validatable";

export default {

	name: 'DynamicFormInput',

	mixins: [Inputtable, Validatable],

	emits: ['update:error', 'key-enter'],

	props: {

		// The type of the input
		type: {
			type: String,
			default: 'input'
		},

		// The options in case the type is 'select'
		options: {
			type: [Array, Number],
			default: () => {
				return []
			}
		},

		// The specific props of the input component
		inputProps: {
			type: Object,
			default: () => {
				return {}
			}
		},

		// Whether to center the input
		center: {
			type: Boolean,
			default: false
		},

		// Whether the input shall retrieve an inverted styling
		inverted: {
			type: Boolean,
			default: false
		},

		// Enables autofocus
		autofocus: {
			type: Boolean,
			default: false
		},

		// Whether to show clear button
		clearable: {
			type: Boolean,
			default: false
		},

		// The name of the input
		name: {
			type: String,
			default: null
		},

		// The placeholder to show on the input
		placeholder: {
			type: String,
			default: null
		},

		// The validation rules
		rules: {
			type: Object,
			default: null
		},

		// The label to show on the input
		label: {
			type: String,
			default: null
		},

		// The help text to show within a help icon
		help: {
			type: String,
			default: null
		},

		// Whether error or info messages shall be hidden.
		// Also removes the placeholders, i.e., the margin, for the messages
		hideDetails: {
			type: Boolean,
			default: false
		},

		color: {
			type: String,
			default: null
		},

		// The info text to show below the input
		info: {
			type: String,
			default: null
		},

		// The error text to show below the input
		error: {
			type: String,
			default: null
		},

		// States if the input shall be disabled
		disabled: {
			type: Boolean,
			default: false
		},
	},

	data() {
		return {
			// The error message to display on the input
			errorMessage: this.error,
		}
	},

	watch: {
		error(error) {
			this.errorMessage = error;
		},

		errorMessage(errorMessage) {
			this.$emit('update:error', errorMessage)
		}
	},

	methods: {
		handleEnter(event = null) {
			this.$emit('key-enter', event);
		},
	}

}
</script>